import { z } from 'zod';

/**
 * How the most recent request's prompt split across the provider's prompt
 * cache. `readTokens` was replayed from an existing cache entry, `writeTokens`
 * was written into the cache by this request, and `freshTokens` was sent
 * uncached; together they make up the request's `usedTokens`. `fraction` is
 * `readTokens / usedTokens` — the share of the prompt served from cache.
 *
 * `ttlTokens` breaks the *written* tokens down by the cache entry's time to
 * live; the buckets sum to `writeTokens`, not to the whole prompt. It is
 * present only for providers that report it (Claude's
 * `cache_creation.ephemeral_{5m,1h}_input_tokens`) and omitted otherwise
 * rather than guessed.
 */
export const ContextCacheUsageSchema = z.object({
  readTokens: z.number().nonnegative(),
  writeTokens: z.number().nonnegative(),
  freshTokens: z.number().nonnegative(),
  fraction: z.number().min(0).max(1),
  ttlTokens: z
    .object({
      ephemeral5m: z.number().nonnegative().optional(),
      ephemeral1h: z.number().nonnegative().optional(),
    })
    .optional(),
});
export type ContextCacheUsage = z.infer<typeof ContextCacheUsageSchema>;

/**
 * A point-in-time snapshot of how full a model's context window is.
 *
 * `usedTokens` is the size of the most recent request's input side (fresh
 * prompt plus any cached prefix the model re-read); `maxTokens` is the
 * model's context window; `fraction` is `usedTokens / maxTokens` clamped to
 * `[0, 1]`. This is the contract a model provider surfaces so the UI can draw
 * a context-usage indicator without re-deriving the math per surface.
 *
 * `cache` optionally carries how that same request split across the prompt
 * cache, for providers that report it.
 */
export const ContextUsageSchema = z.object({
  usedTokens: z.number().nonnegative(),
  maxTokens: z.number().positive(),
  fraction: z.number().min(0).max(1),
  cache: ContextCacheUsageSchema.optional(),
});
export type ContextUsage = z.infer<typeof ContextUsageSchema>;

/**
 * The input-side token fields read off a Claude-style usage object to size the
 * current context. Mirrors the relevant subset of the protocol's
 * `AssistantUsage`; kept structural so callers can pass a raw payload without
 * importing the full protocol schema.
 */
export interface ContextUsageTokenInput {
  input_tokens?: number | null;
  cache_read_input_tokens?: number | null;
  cache_creation_input_tokens?: number | null;
  cache_creation?: {
    ephemeral_5m_input_tokens?: number | null;
    ephemeral_1h_input_tokens?: number | null;
  } | null;
}

function positiveOrZero(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Sum the input-side tokens that make up the current context window: the fresh
 * input plus any cached prefix the model re-read for this turn. Output tokens
 * are excluded because they describe the response, not the prompt that fills
 * the window. Returns 0 for a missing or empty usage object.
 */
export function contextTokensFromUsage(usage: ContextUsageTokenInput | null | undefined): number {
  if (!usage) return 0;
  return (
    positiveOrZero(usage.input_tokens) +
    positiveOrZero(usage.cache_read_input_tokens) +
    positiveOrZero(usage.cache_creation_input_tokens)
  );
}

/**
 * Split a Claude-style usage object into its prompt-cache breakdown. Returns
 * `null` when the request touched the cache in neither direction (no reads and
 * no writes), so a provider that does not use prompt caching — or a first,
 * cold request — surfaces no cache stats rather than a row of zeroes.
 */
export function deriveCacheUsage(
  usage: ContextUsageTokenInput | null | undefined,
): ContextCacheUsage | null {
  if (!usage) return null;
  const readTokens = positiveOrZero(usage.cache_read_input_tokens);
  const writeTokens = positiveOrZero(usage.cache_creation_input_tokens);
  if (readTokens === 0 && writeTokens === 0) return null;

  const freshTokens = positiveOrZero(usage.input_tokens);
  // Same sum the meter sizes itself with, so the breakdown always adds up to
  // the `usedTokens` shown beside it.
  const total = contextTokensFromUsage(usage);
  const ephemeral5m = positiveOrZero(usage.cache_creation?.ephemeral_5m_input_tokens);
  const ephemeral1h = positiveOrZero(usage.cache_creation?.ephemeral_1h_input_tokens);

  return {
    readTokens,
    writeTokens,
    freshTokens,
    // `total` is read + write + fresh, so the share can never exceed 1.
    fraction: total > 0 ? readTokens / total : 0,
    ...(ephemeral5m > 0 || ephemeral1h > 0
      ? {
          ttlTokens: {
            ...(ephemeral5m > 0 ? { ephemeral5m } : {}),
            ...(ephemeral1h > 0 ? { ephemeral1h } : {}),
          },
        }
      : {}),
  };
}

/**
 * Derive a context-usage snapshot from a used-token count and the model's
 * context window, optionally carrying the prompt-cache breakdown of the same
 * request. Returns `null` when the inputs cannot produce a meaningful
 * indicator (no/zero window, or a non-finite or negative token count) so
 * callers can simply hide the meter rather than branch on edge cases.
 */
export function deriveContextUsage(
  usedTokens: number,
  maxTokens: number | null | undefined,
  cache?: ContextCacheUsage | null,
): ContextUsage | null {
  if (typeof maxTokens !== 'number' || !Number.isFinite(maxTokens) || maxTokens <= 0) {
    return null;
  }
  if (!Number.isFinite(usedTokens) || usedTokens < 0) {
    return null;
  }
  return {
    usedTokens,
    maxTokens,
    fraction: Math.min(1, usedTokens / maxTokens),
    ...(cache ? { cache } : {}),
  };
}
